/**
 * Geocentric Conversion for All Planets
 * 
 * Converts heliocentric positions to geocentric
 * - Inner planets (Mercury, Venus): Significant correction needed
 * - Outer planets (Jupiter, Saturn, etc.): Smaller but still important correction
 * Based on Meeus "Astronomical Algorithms" Chapter 33
 */

import { normalizeAngle } from './astronomy-core';
import { calculateSunPosition } from './calculator';

/**
 * Calculate heliocentric distance from mean anomaly and eccentricity
 * r = a * (1 - e * cos(E))
 * where a is semi-major axis, e is eccentricity, E is eccentric anomaly
 */
function calculateHeliocentricDistance(
  E: number, // Eccentric anomaly in radians
  e: number, // Eccentricity
  a: number  // Semi-major axis in AU
): number {
  return a * (1 - e * Math.cos(E));
}

/**
 * Convert heliocentric longitude to geocentric
 * 
 * Uses proper geometric conversion based on elongation and distances
 * Handles both inner and outer planets correctly
 */
/**
 * Convert heliocentric to geocentric using proper 3D vector math
 * 
 * This is the CORRECT method for accurate geocentric positions
 */
export function heliocentricToGeocentric(
  helioLongitude: number,
  planetName: string,
  date: Date,
  helioDistance?: number // Optional: actual heliocentric distance in AU
): number {
  // Earth's heliocentric longitude = Sun's geocentric + 180°
  const sunGeocentric = calculateSunPosition(date);
  const earthHeliocentric = normalizeAngle(sunGeocentric + 180);
  
  // Use actual distance if provided, otherwise use average
  let planetDistance = 0;
  if (helioDistance) {
    planetDistance = helioDistance;
  } else {
    // Average distances from Sun (in AU) - fallback
    if (planetName === 'Mercury') {
      planetDistance = 0.387;
    } else if (planetName === 'Venus') {
      planetDistance = 0.723;
    } else if (planetName === 'Mars') {
      planetDistance = 1.52;
    } else if (planetName === 'Jupiter') {
      planetDistance = 5.20;
    } else if (planetName === 'Saturn') {
      planetDistance = 9.58;
    } else {
      planetDistance = 1.0; // Default
    }
  }
  
  // Earth's distance from Sun (approximately 1.0 AU)
  const earthDistance = 1.0; // AU
  
  // Convert to radians
  const planetHelioRad = helioLongitude * Math.PI / 180;
  const earthHelioRad = earthHeliocentric * Math.PI / 180;
  
  // Convert to 3D rectangular coordinates (heliocentric, ecliptic plane)
  // Planet's position vector
  const planetX = planetDistance * Math.cos(planetHelioRad);
  const planetY = planetDistance * Math.sin(planetHelioRad);
  
  // Earth's position vector
  const earthX = earthDistance * Math.cos(earthHelioRad);
  const earthY = earthDistance * Math.sin(earthHelioRad);
  
  // Geocentric position vector (vector subtraction)
  const geoX = planetX - earthX;
  const geoY = planetY - earthY;
  
  // Convert back to longitude
  const geoLongitude = normalizeAngle(Math.atan2(geoY, geoX) * 180 / Math.PI);
  
  return geoLongitude;
}
